import { useState, useCallback } from 'react';
import { callStartSession, callSendMessage, callProcessExtraction } from '../services/functions';
import { abandonSession, getMessages, getSession, updateSession } from '../services/sessions';
import type { SessionType, SessionOutputs, RealTimeExtraction } from '../types';

export interface LocalMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ExtractionSummary {
  nodesCreated: number;
  edgesCreated: number;
  motifsIdentified: number;
  updatedNodeCount: number;
  updatedEdgeCount: number;
}

type SessionStatus = 'idle' | 'starting' | 'active' | 'sending' | 'ending' | 'completed';

export interface UseSessionReturn {
  sessionId: string | null;
  sessionType: SessionType | null;
  messages: LocalMessage[];
  realtimeExtractions: RealTimeExtraction[];
  extractionSummary: ExtractionSummary | null;
  status: SessionStatus;
  error: string | null;
  start: (type: SessionType) => Promise<void>;
  resume: (sessionId: string) => Promise<void>;
  send: (content: string) => Promise<void>;
  end: (outputs?: SessionOutputs) => Promise<void>;
  discard: () => Promise<void>;
  reset: () => void;
}

function timestampToDate(timestamp: { toDate?: () => Date; seconds?: number } | Date): Date {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp?.toDate) return timestamp.toDate();
  if (typeof timestamp?.seconds === 'number') return new Date(timestamp.seconds * 1000);
  return new Date();
}

export function useSession(userId: string | null): UseSessionReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionType, setSessionType] = useState<SessionType | null>(null);
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [realtimeExtractions, setRealtimeExtractions] = useState<RealTimeExtraction[]>([]);
  const [extractionSummary, setExtractionSummary] = useState<ExtractionSummary | null>(null);
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async (type: SessionType) => {
    setStatus('starting');
    setError(null);
    setMessages([]);
    setRealtimeExtractions([]);
    setExtractionSummary(null);

    try {
      const result = await callStartSession(type, 'text');
      setSessionId(result.sessionId);
      setSessionType(type);
      setMessages([
        {
          id: `msg-0`,
          role: 'assistant',
          content: result.openingMessage,
          timestamp: new Date(),
        },
      ]);
      setStatus('active');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
      setStatus('idle');
    }
  }, []);

  const send = useCallback(async (content: string) => {
    if (!sessionId || status !== 'active') return;

    const userMsg: LocalMessage = {
      id: `msg-user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setStatus('sending');
    setError(null);

    try {
      const result = await callSendMessage(sessionId, content);

      const assistantMsg: LocalMessage = {
        id: `msg-assistant-${result.messageIndex}`,
        role: 'assistant',
        content: result.assistantMessage,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMsg]);

      if (result.realtimeExtractions.length > 0) {
        setRealtimeExtractions(prev => {
          // Deduplicate by content
          const existing = new Set(prev.map(e => e.content));
          const newOnes = result.realtimeExtractions.filter(e => !existing.has(e.content));
          return [...prev, ...newOnes];
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
      // Remove the optimistically added user message on failure
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
    } finally {
      setStatus('active');
    }
  }, [sessionId, status]);

  const resume = useCallback(async (existingSessionId: string) => {
    if (!userId) {
      setError('You must be signed in to continue a conversation');
      setStatus('idle');
      return;
    }

    setStatus('starting');
    setError(null);
    setRealtimeExtractions([]);
    setExtractionSummary(null);

    try {
      const [existingSession, existingMessages] = await Promise.all([
        getSession(userId, existingSessionId),
        getMessages(userId, existingSessionId),
      ]);

      if (!existingSession) {
        throw new Error('Conversation not found');
      }

      await updateSession(userId, existingSessionId, { status: 'active' });

      setSessionId(existingSessionId);
      setSessionType(existingSession.type);
      setMessages(
        existingMessages.map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: timestampToDate(msg.timestamp),
        }))
      );
      setStatus('active');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to continue conversation');
      setStatus('idle');
    }
  }, [userId]);

  const end = useCallback(async (outputs: SessionOutputs = {}) => {
    if (!sessionId) return;

    const sid = sessionId;
    if (userId) {
      try {
        // Mark extracting before navigation so Home can show processing feedback immediately.
        await updateSession(userId, sid, { status: 'extracting' });
      } catch (err) {
        console.warn('Failed to mark session extracting before close:', err);
      }
    }

    setStatus('completed');

    // Run extraction in the background — don't block the user
    callProcessExtraction(sid, outputs)
      .then((summary) => {
        setExtractionSummary({
          nodesCreated: summary.nodesCreated,
          edgesCreated: summary.edgesCreated,
          motifsIdentified: summary.motifsIdentified,
          updatedNodeCount: summary.updatedNodeCount,
          updatedEdgeCount: summary.updatedEdgeCount,
        });
      })
      .catch((err) => {
        console.error('Background extraction failed:', err);
      });
  }, [sessionId, userId]);

  const discard = useCallback(async () => {
    if (!userId || !sessionId) return;
    setStatus('ending');
    setError(null);
    try {
      await abandonSession(userId, sessionId);
      setStatus('completed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discard conversation');
      setStatus('active');
      throw err;
    }
  }, [userId, sessionId]);

  const reset = useCallback(() => {
    setSessionId(null);
    setSessionType(null);
    setMessages([]);
    setRealtimeExtractions([]);
    setExtractionSummary(null);
    setStatus('idle');
    setError(null);
  }, []);

  return {
    sessionId,
    sessionType,
    messages,
    realtimeExtractions,
    extractionSummary,
    status,
    error,
    start,
    resume,
    send,
    end,
    discard,
    reset,
  };
}
